import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import asset components (lazy-loaded for performance)
const AssetsList = React.lazy(() => import("./AssetsList"));
const NewAsset = React.lazy(() => import("./NewAsset"));
const AssetDetails = React.lazy(() => import("./AssetDetails"));
const AssetTypes = React.lazy(() => import("./AssetTypes"));

const assetsModuleConfig: ModuleConfig = {
    // Module Identity
    id: "assets",
    name: "Asset Management",
    description: "Track and manage company assets with QR codes",
    version: "1.0.0",
    enabled: true,
    category: "operations",
    order: 5,
    icon: "🏷️",
    color: "#10B981",

    // Routes
    routes: [
        {
            path: "/assets",
            component: AssetsList,
            permissions: ["ADMIN", "MANAGER", "ASSET_VIEWER", "EMPLOYEE"],
            useLayout: true
        },
        {
            path: "/assets/new",
            component: NewAsset,
            permissions: ["ADMIN", "MANAGER", "ASSET_MANAGER"],
            useLayout: true
        },
        {
            path: "/assets/types",
            component: AssetTypes,
            permissions: ["ADMIN", "ASSET_MANAGER"],
            useLayout: true
        },
        {
            path: "/assets/:id",
            component: AssetDetails,
            permissions: ["ADMIN", "MANAGER", "ASSET_VIEWER", "EMPLOYEE"],
            useLayout: true
        },
        {
            path: "/assets/:id/qr",
            component: AssetDetails, // Shows QR modal
            permissions: ["ADMIN", "MANAGER", "ASSET_VIEWER", "EMPLOYEE"],
            useLayout: true
        },
        {
            path: "/assets/:id/maintenance",
            component: AssetDetails, // Opens with log modal
            permissions: ["ADMIN", "MANAGER", "ASSET_MANAGER", "EMPLOYEE"],
            useLayout: true
        },
        {
            path: "/assets/edit/:id",
            component: NewAsset, // Reuse for editing
            permissions: ["ADMIN", "MANAGER", "ASSET_MANAGER"],
            useLayout: true
        }
    ],

    // Navigation
    navigation: [
        {
            label: "All Assets",
            path: "/assets",
            icon: "📦",
            permissions: ["ADMIN", "MANAGER", "ASSET_VIEWER", "EMPLOYEE"]
        },
        {
            label: "Add Asset",
            path: "/assets/new",
            icon: "➕",
            permissions: ["ADMIN", "MANAGER", "ASSET_MANAGER"]
        },
        {
            label: "Asset Types",
            path: "/assets/types",
            icon: "⚙️",
            permissions: ["ADMIN", "ASSET_MANAGER"]
        }
    ],

    // Permissions
    permissions: {
        view: ["ADMIN", "MANAGER", "ASSET_VIEWER", "EMPLOYEE"],
        create: ["ADMIN", "MANAGER", "ASSET_MANAGER"],
        edit: ["ADMIN", "MANAGER", "ASSET_MANAGER"],
        delete: ["ADMIN", "ASSET_MANAGER"]
    },

    // Quick Actions
    quickActions: [
        {
            label: "Add Asset",
            path: "/assets/new",
            icon: "➕",
            description: "Register a new asset with QR code"
        },
        {
            label: "View All",
            path: "/assets",
            icon: "📦",
            description: "View all company assets"
        },
        {
            label: "Asset Types",
            path: "/assets/types",
            icon: "⚙️",
            description: "Configure asset categories"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready asset management system",
                "✅ QR code generation for asset tracking",
                "✅ Maintenance logging and history",
                "✅ Asset type customization",
                "✅ Advanced search and filtering",
                "✅ Role-based permissions (ASSET_MANAGER, ASSET_VIEWER)",
                "✅ Mobile-responsive asset viewing"
            ],
            notes: "Official stable release with comprehensive asset tracking capabilities."
        },
        {
            version: "0.4.0",
            releaseDate: "2025-09-21",
            title: "Video Management Integration",
            features: [
                "✨ Enhanced AssetDetails with video support (2 lines)",
                "✨ Video upload for asset documentation",
                "✨ Asset type video enhancements (2 lines)",
                "✨ Improved asset list with media (2 lines)",
                "✨ New asset form video integration (3 lines)"
            ],
            notes: "Video management capabilities for better asset documentation."
        },
        {
            version: "0.3.0",
            releaseDate: "2025-09-20",
            title: "Module System & Theme Enhancements",
            features: [
                "✨ Full light/dark mode support (119 lines)",
                "✨ Enhanced AssetsList theme awareness (57 lines)",
                "✨ Improved NewAsset color modes (53 lines)",
                "✨ Updated AssetTypes styling (30 lines)",
                "✨ Better AssetDetails themes (22 lines)",
                "✨ Module import improvements (19 lines)"
            ],
            notes: "Complete theme system and module architecture improvements."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-12-10",
            title: "Search & Filter Enhancements",
            features: [
                "✨ Advanced asset search capabilities",
                "✨ Multi-criteria filtering",
                "✨ Asset status tracking",
                "✨ Improved asset organization",
                "✨ Better mobile responsiveness"
            ],
            notes: "Enhanced search and filtering for better asset discovery."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-08-28",
            title: "Initial Asset Management System",
            features: [
                "✅ Created AssetDetails component (1,313 lines)",
                "✅ Created NewAsset component (824 lines)",
                "✅ Created AssetsList component (657 lines)",
                "✅ Created AssetTypes component (517 lines)",
                "✅ Module configuration and routing (114 lines)",
                "✅ QR code generation and scanning",
                "✅ Maintenance log tracking",
                "✅ Asset assignment and transfer",
                "✅ Custom asset type creation"
            ],
            notes: "Initial implementation with 3,425 lines of asset management code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default assetsModuleConfig;